import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { CountryData } from '@components/country-code/types';
import { FEATURE_FLAGS } from '@constants/feature';
import { allowedCSVSizeForGroupCreation } from '@constants/file';
import { GROUP_MEMBER_MAX_COUNT } from '@constants/generic';
import { MixpanelEventV2, sendMixpanelEventV2 } from '@core/analytics/mixpanel';
import { UserProfileCustomField } from '@features/member-management/types';
import useNotify from '@hooks/notify';
import useActiveNotificationChannel from '@hooks/useActiveNotificationChannel';
import {
  AddMemberInput,
  GroupFeeConfigurations,
  MemberListType,
  TermData
} from '@types';
import { formatISODate } from '@utils/date';
import { bytesToMB, parseCSVFile, parseXLSXFile } from '@utils/file';
import { roundTo2 } from '@utils/generic';
import { snakeToCamel } from '@utils/stringFormat';

import { PaymentCollectionType } from '../constants';
import {
  getFileImportPaymentSchedule,
  getMonthConfigFromDate,
  parseDateString
} from '../helper';
import {
  buildFeesInfo,
  buildFeesInfoCategoryAndFlatStructure,
  buildFormattedMemberData,
  getFeeCategoryData,
  processMemberInfo,
  sanitizeMemberInfo
} from '../helpers/importFile';
import { getValidationErrorsOnMemberDetails } from '../helpers/memberFileValidation';
import { useAddMemberToGroupV3Mutation } from '../queries/GroupQuery';
import { GroupMemberError, GroupMemberResponseItem } from '../types';

export type UploadStep = 'upload' | 'error_preview' | 'warning_preview';

const getImportFileType = (file: File): string => {
  if (file.type === 'text/csv') return 'csv';
  if (
    file.type ===
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ) {
    return 'xlsx';
  }
  const ext = file.name.split('.').pop()?.toLowerCase();
  if (ext === 'csv') return 'csv';
  if (ext === 'xlsx') return 'xlsx';
  return 'unknown';
};

// Mapping from backend field names to display field names
const BACKEND_TO_DISPLAY_FIELD_MAP: Record<string, string> = {
  name: 'name',
  country_code: 'primaryNumberCode',
  mobile: 'primaryNumber',
  secondary_country_code: 'altNumberCode',
  alternate_mobile: 'altNumber',
  email: 'email',
  date_of_birth: 'dateOfBirth',
  guardian_name: 'guardianName',
  admission_number: 'admissionNumber',
  admission_date: 'admissionDate',
  address_line_1: 'addressLine1',
  address_line_2: 'addressLine2',
  country: 'country',
  state: 'state',
  city: 'city',
  pincode: 'pincode',
  amount: 'amount'
};

const BASE_WARNING_PREVIEW_HEADERS = [
  'name',
  'primaryNumberCode',
  'primaryNumber',
  'altNumberCode',
  'altNumber',
  'email',
  'amount',
  'admissionNumber',
  'admissionDate',
  'dateOfBirth',
  'guardianName',
  'addressLine1',
  'addressLine2',
  'city',
  'state',
  'country',
  'pincode'
];

const getWarningPreviewHeaders = (
  isAttendanceBasedFee: boolean,
  orgCustomFields: UserProfileCustomField[] = []
) => {
  const headers = [...BASE_WARNING_PREVIEW_HEADERS];
  if (isAttendanceBasedFee) {
    const amountIndex = headers.indexOf('amount');
    headers.splice(amountIndex + 1, 0, 'billingPeriod');
  }
  orgCustomFields.forEach((field) => {
    headers.push(snakeToCamel(field.key));
  });
  return headers;
};

// Transform input row to display format for ParsedDataGrid
export const transformInputRowToDisplayRow = (
  row: AddMemberInput,
  index: number
): Record<string, any> => ({
  __rowId: `row-${index}`,
  ...row
});

// Transform MemberResponseItem (backend format) to display format
export const transformMemberResponseToDisplayRow = (
  member: GroupMemberResponseItem,
  index: number
): Record<string, any> => {
  const displayRow: Record<string, any> = {
    __rowId: `row-${index}`
  };

  Object.entries(member).forEach(([key, value]) => {
    if (
      key === 'errors' ||
      key === 'custom_fields' ||
      key === 'payment_schedule'
    ) {
      return;
    }
    const displayKey = BACKEND_TO_DISPLAY_FIELD_MAP[key] || key;
    displayRow[displayKey] = value ?? '';
  });

  if (member.payment_schedule) {
    const ps = member.payment_schedule;
    if (ps.sessions_per_cycle) {
      displayRow.billingPeriod = `${ps.sessions_per_cycle} classes`;
    } else if (ps.monthly_config) {
      if (ps.monthly_config.day_option === 'first_day') {
        displayRow.billingPeriod = '1st day of month';
      } else if (ps.monthly_config.day_option === 'last_day') {
        displayRow.billingPeriod = 'Last day of month';
      } else if (ps.monthly_config.custom_day) {
        displayRow.billingPeriod = `${ps.monthly_config.custom_day}th day of month`;
      }
    }
  }

  if (member.custom_fields) {
    member.custom_fields.forEach((cf) => {
      displayRow[snakeToCamel(cf.key)] = cf.value ?? '';
    });
  }

  return displayRow;
};

// Transform errors to rowErrorsById format for ParsedDataGrid
export const transformErrorsToRowErrorsById = (
  members: Array<{ errors: GroupMemberError[] | null }>,
  startIndex = 0
): Record<string, string[]> => {
  const rowErrorsById: Record<string, string[]> = {};
  members.forEach((member, index) => {
    if (member.errors && member.errors.length > 0) {
      rowErrorsById[`row-${startIndex + index}`] = member.errors.map(
        (err) => `${err.message}`
      );
    }
  });
  return rowErrorsById;
};

// Convert FE validation error strings to MemberError format per member
const convertFEErrorsToMemberErrors = (
  feErrors: string[],
  memberCount: number
): GroupMemberError[][] => {
  const memberErrors: GroupMemberError[][] = Array(memberCount)
    .fill(null)
    .map(() => []);

  feErrors.forEach((error) => {
    // Parse "Row X: message" format
    const match = error.match(/^Row (\d+): (.+)$/);
    if (match) {
      const rowIndex = parseInt(match[1], 10) - 1; // Convert to 0-based index
      const message = match[2];
      if (rowIndex >= 0 && rowIndex < memberCount) {
        // Try to extract field name from the error message
        let field = 'general';
        if (message.toLowerCase().includes('name')) field = 'name';
        else if (message.toLowerCase().includes('primary number'))
          field = 'primaryNumber';
        else if (message.toLowerCase().includes('alternative number'))
          field = 'altNumber';
        else if (message.toLowerCase().includes('email')) field = 'email';
        else if (message.toLowerCase().includes('amount')) field = 'amount';
        else if (message.toLowerCase().includes('date of birth'))
          field = 'dateOfBirth';
        else if (message.toLowerCase().includes('admission'))
          field = 'admissionNumber';
        else if (message.toLowerCase().includes('guardian'))
          field = 'guardianName';
        else if (message.toLowerCase().includes('country')) field = 'country';
        else if (message.toLowerCase().includes('state')) field = 'state';
        else if (message.toLowerCase().includes('city')) field = 'city';
        else if (message.toLowerCase().includes('pincode')) field = 'pincode';

        memberErrors[rowIndex].push({ field, message });
      }
    }
  });

  return memberErrors;
};

// Merge BE errors back to members with FE errors
const mergeBackendErrors = (
  membersWithFEErrors: Array<{ errors: GroupMemberError[] }>,
  beMembers: GroupMemberResponseItem[],
  beWarnings: GroupMemberResponseItem[],
  feValidIndices: number[]
): Array<{ errors: GroupMemberError[] }> => {
  const merged = membersWithFEErrors.map((m) => ({
    ...m,
    errors: [...m.errors]
  }));

  // Merge errors from BE members response
  beMembers.forEach((beMember, beIndex) => {
    const originalIndex = feValidIndices[beIndex];
    if (
      originalIndex !== undefined &&
      beMember.errors &&
      beMember.errors.length > 0
    ) {
      merged[originalIndex].errors = [
        ...merged[originalIndex].errors,
        ...beMember.errors
      ];
    }
  });

  return merged;
};

// Get indices of members without FE errors
const getMembersWithoutErrorsIndices = (
  memberErrors: GroupMemberError[][]
): number[] =>
  memberErrors
    .map((errors, index) => (errors.length === 0 ? index : -1))
    .filter((index) => index !== -1);

interface UseGroupFileUploadProps {
  organizationId: string;
  branchId: string;
  groupId: string;
  groupFeeConfigurations?: GroupFeeConfigurations[];
  isOnce: boolean;
  groupFrequency?: string;
  groupIntervalFrequency?: number;
  isCustomTerm?: boolean;
  termsData?: TermData[];
  groupStartDate?: string;
  groupEndDate?: string;
  isImmediatePayment?: boolean;
  groupAmount?: string;
  isFeeCategoryEnabled?: boolean;
  isAttendanceBasedFee?: boolean;
  isSessionBased?: boolean;
  groupSessionsPerCycle?: number;
  onProceed: (members: MemberListType[]) => void;
  defaultTermLabel?: string;
  mixpanelGroupContext?: Record<string, unknown>;
}

interface UseGroupFileUploadReturn {
  // Step state
  step: UploadStep;

  // File state
  file: File | undefined;
  fileName: string;
  fileSizeInMB: string;
  fileUploadError: string;
  isLoading: boolean;
  timeLeft: number;
  totalTime: number;
  showPrimaryNumberWarning: boolean;
  headers: string[]; // Original file headers for View File
  displayHeaders: string[]; // Headers for modal display (changes based on step)
  rows: AddMemberInput[];

  // Response data for ParsedDataGrid
  displayRows: Record<string, any>[];
  rowErrorsById: Record<string, string[]>;

  // Computed counts
  totalMembers: number;
  membersWithErrors: number;
  membersWithoutErrors: number;
  warningCount: number;

  // UI labels
  modalTitle: string;
  primaryButtonLabel: string | undefined;
  isPrimaryButtonDisabled: boolean;

  // Loading state
  isApiLoading: boolean;

  // Handlers
  onFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  checkFileValidityAndProcess: (file: File) => boolean;
  deleteFile: () => void;
  handleCancel: () => void;
  handleNextClick: () => void;
  handleProceedClick: () => void;
}

// eslint-disable-next-line import/prefer-default-export
export const useGroupFileUpload = ({
  organizationId,
  branchId,
  groupId,
  groupFeeConfigurations,
  isOnce,
  groupFrequency,
  groupIntervalFrequency,
  isCustomTerm,
  termsData,
  groupStartDate,
  groupEndDate,
  isImmediatePayment,
  groupAmount,
  isFeeCategoryEnabled,
  isAttendanceBasedFee,
  isSessionBased,
  groupSessionsPerCycle,
  onProceed,
  defaultTermLabel,
  mixpanelGroupContext = {}
}: UseGroupFileUploadProps): UseGroupFileUploadReturn => {
  const { orgConfig, appConfig, orgFeatureFlags } = useSelector(
    (state: any) => state.user
  );
  const { memberCustomFields } = useSelector(
    (state: any) => state.branchConfig
  );
  const { createSuccessAlert, createErrorAlert } = useNotify();
  const { isNotificationChannelActive } = useActiveNotificationChannel();

  const groupMemberMaxAmount = orgConfig?.group_member_amount_max || 1000000;
  const groupMemberMinAmount = orgConfig?.group_member_amount_min || 1;
  const isEmailEnabled = isNotificationChannelActive(
    'payment_request',
    'email'
  );
  const isAmountPaidTillDateEnabled =
    !!orgConfig?.is_amount_paid_till_date_enabled && !groupId;
  const isLeadSquared = orgFeatureFlags?.includes(FEATURE_FLAGS.LEAD_SQUARED);
  const isEndDateEnabled = orgFeatureFlags?.includes(
    FEATURE_FLAGS.MEMBER_END_DATE
  );
  const customFields = memberCustomFields as UserProfileCustomField[];

  const feeCategories = useMemo(
    () => groupFeeConfigurations?.map((value) => value.category) || [],
    [groupFeeConfigurations]
  );
  const termPrefix = defaultTermLabel || 'Installment';
  const termsName = useMemo(
    () =>
      termsData?.map(
        (item) => item.label || `${termPrefix} ${item.term_number}`
      ) || [],
    [termsData, termPrefix]
  );

  // Step state
  const [step, setStep] = useState<UploadStep>('upload');

  // File upload state
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fileUploadError, setFileUploadError] = useState<string>('');
  const [file, setFile] = useState<File>();
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<AddMemberInput[]>([]);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [totalTime, setTotalTime] = useState<number>(0);
  const [fileName, setFileName] = useState<string>('');
  const [fileSizeInMB, setFileSizeInMB] = useState<string>('0 MB');
  const [showPrimaryNumberWarning, setShowPrimaryNumberWarning] =
    useState<boolean>(false);

  // Member data with errors
  const [membersWithErrors, setMembersWithErrors] = useState<
    Array<{ errors: GroupMemberError[] }>
  >([]);
  const [warningRecords, setWarningRecords] = useState<
    GroupMemberResponseItem[]
  >([]);
  // Store formatted member data for final proceed
  const [formattedMembers, setFormattedMembers] = useState<MemberListType[]>(
    []
  );

  const [
    addMemberToGroupV3,
    { isLoading: isApiLoading, reset: resetApiState }
  ] = useAddMemberToGroupV3Mutation();

  const mixpanelGroupContextRef = useRef(mixpanelGroupContext);
  mixpanelGroupContextRef.current = mixpanelGroupContext;

  const trackImportFileUploaded = (
    selectedFile: File,
    status: 'success' | 'failure',
    failureReason?: string
  ) => {
    sendMixpanelEventV2(MixpanelEventV2.GROUP_MEMBER_IMPORT_FILE_UPLOADED, {
      status,
      file_type: getImportFileType(selectedFile),
      ...(failureReason ? { failure_reason: failureReason } : {}),
      ...mixpanelGroupContextRef.current
    });
  };

  const getImportRowCounts = (
    members: Array<{ errors?: GroupMemberError[] }>
  ) => {
    const totalRows = members.length;
    const errorRows = members.filter(
      (member) => member.errors && member.errors.length > 0
    ).length;
    return {
      total_rows: totalRows,
      valid_rows: totalRows - errorRows,
      error_rows: errorRows
    };
  };

  const trackImportPreviewViewed = (
    selectedFile: File,
    members: Array<{ errors?: GroupMemberError[] }>
  ) => {
    sendMixpanelEventV2(
      MixpanelEventV2.GROUP_MEMBER_IMPORT_FILE_PREVIEW_VIEWED,
      {
        file_type: getImportFileType(selectedFile),
        ...getImportRowCounts(members),
        ...mixpanelGroupContextRef.current
      }
    );
  };

  useEffect(() => {
    if (
      (step === 'error_preview' || step === 'warning_preview') &&
      file &&
      membersWithErrors.length > 0
    ) {
      trackImportPreviewViewed(file, membersWithErrors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Countdown timer for file upload
  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (isLoading) {
      interval = setInterval(() => {
        setTimeLeft((prevTime) => (prevTime > 0 ? prevTime - 1 : 0));
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isLoading]);

  const countryCodeList = (
    appConfig?.supported_phone_number_countries || []
  ).map((country: CountryData) => `${country.dial_code}`);

  const countryCodeMapping = (
    appConfig?.supported_phone_number_countries || []
  ).reduce((acc: Record<string, string>, country: CountryData) => {
    acc[country.dial_code.toString()] = country.code;
    return acc;
  }, {});

  const resetState = () => {
    setStep('upload');
    setFile(undefined);
    setHeaders([]);
    setRows([]);
    setMembersWithErrors([]);
    setWarningRecords([]);
    setFormattedMembers([]);
    setFileName('');
    setFileSizeInMB('0 MB');
    setFileUploadError('');
    setShowPrimaryNumberWarning(false);
    resetApiState();
  };

  const deleteFile = () => {
    resetState();
  };

  const handleCancel = () => {
    if (step !== 'upload') {
      sendMixpanelEventV2(MixpanelEventV2.GROUP_MEMBER_IMPORT_FILE_CANCELLED, {
        ...mixpanelGroupContextRef.current
      });
    }
    resetState();
  };

  const handleFileProcessing = async (selectedFile: File) => {
    setFileUploadError('');
    setShowPrimaryNumberWarning(false);
    setWarningRecords([]);

    try {
      let parsedData;
      if (selectedFile.type === 'text/csv') {
        parsedData = await parseCSVFile(selectedFile);
      } else {
        parsedData = await parseXLSXFile(selectedFile);
      }

      const memberInfo = parsedData.data as unknown as AddMemberInput[];

      // Sanitize member info (trim strings, round amounts to 2 decimals)
      sanitizeMemberInfo(memberInfo, feeCategories, termsName);

      // FE Validation - collect errors per member (don't block)
      const feErrors = getValidationErrorsOnMemberDetails(
        parsedData.meta.fields || [],
        memberInfo,
        Number(groupMemberMaxAmount),
        Number(groupMemberMinAmount),
        feeCategories,
        isEmailEnabled,
        isAmountPaidTillDateEnabled,
        isOnce,
        customFields,
        countryCodeList,
        countryCodeMapping,
        termsName,
        isCustomTerm || false,
        isLeadSquared,
        isEndDateEnabled,
        groupEndDate,
        groupStartDate,
        isAttendanceBasedFee,
        isSessionBased,
        termsData
      );

      // Check for header errors (these are blocking)
      if (
        feErrors.length === 1 &&
        feErrors[0].includes("Column names don't match")
      ) {
        setFileUploadError(feErrors[0]);
        setIsLoading(false);
        trackImportFileUploaded(
          selectedFile,
          'failure',
          'column_names_mismatch'
        );
        return;
      }

      setFile(selectedFile);
      setHeaders(
        parsedData.meta.fields?.filter(
          (header) => header !== '' && !header.startsWith('_')
        ) || []
      );

      // Process member info (fee categories, dates, frequencies)
      const hasPrimaryNumberError = processMemberInfo(
        memberInfo,
        feeCategories,
        groupFrequency,
        groupIntervalFrequency,
        groupFeeConfigurations,
        termsName
      );
      setShowPrimaryNumberWarning(hasPrimaryNumberError);

      // Map attendance-based fields to standard member fields
      if (isAttendanceBasedFee) {
        memberInfo.forEach((member: any) => {
          member.amount =
            member.ratePerClass !== null &&
            member.ratePerClass !== undefined &&
            member.ratePerClass !== ''
              ? `${roundTo2(Number(member.ratePerClass))}`
              : groupAmount || '';
          if (isSessionBased) {
            member.sessionsPerCycle =
              Number(member['billingPeriod(numberOfClasses)']) ||
              groupSessionsPerCycle ||
              0;
          }
        });
      }

      // Build formatted member data for later use
      const updatedMemberList = memberInfo.map((member) =>
        buildFormattedMemberData(
          member,
          termsName,
          termsData,
          feeCategories,
          isCustomTerm,
          groupStartDate,
          isLeadSquared,
          isEndDateEnabled || false,
          isAttendanceBasedFee,
          isSessionBased,
          defaultTermLabel,
          customFields || []
        )
      ) as MemberListType[];
      setFormattedMembers(updatedMemberList);

      // Format rows for display
      const formattedRows = memberInfo.map((row) => {
        const feesInfo = buildFeesInfo(row, termsData, defaultTermLabel);
        const { formattedFeesInfo } = buildFeesInfoCategoryAndFlatStructure(
          row,
          termsData,
          termsName
        );

        return {
          ...row,
          startDate: row.startDate
            ? (() => {
                const { date } = parseDateString(row.startDate);
                return date
                  ? `${date.getFullYear()}-${String(
                      date.getMonth() + 1
                    ).padStart(2, '0')}-${String(date.getDate()).padStart(
                      2,
                      '0'
                    )}`
                  : '';
              })()
            : '',
          ...(isCustomTerm && feeCategories.length === 0
            ? { ...feesInfo }
            : { ...formattedFeesInfo })
        };
      });
      setRows(formattedRows);

      // Convert FE errors to per-member format
      const memberFEErrors = convertFEErrorsToMemberErrors(
        feErrors,
        memberInfo.length
      );

      // Initialize members with FE errors
      const membersData = memberInfo.map((_, index) => ({
        errors: memberFEErrors[index] || []
      }));

      // Get indices of members without FE errors for BE call
      const feValidIndices = getMembersWithoutErrorsIndices(memberFEErrors);

      // Call BE API with force_create: false for FE-valid members only
      if (feValidIndices.length > 0) {
        // Build request body for valid members only - include all fields like transformMembersArray
        const validMembersPayload = feValidIndices.map((index) => {
          const member = updatedMemberList[index];
          const getMemberAmount = () => {
            if (member?.amount || member?.amount === '0') {
              return member.amount;
            }
            if (groupAmount) {
              return groupAmount;
            }
            return '0';
          };
          const memberAmount = getMemberAmount();

          return {
            name: member.name?.trim() || '',
            mobile: member.primaryNumber?.toString().replace(/\s+/g, '') || '',
            country_code: member.primaryNumberCountryCode || '',
            alternate_mobile:
              member.altNumber?.toString().replace(/\s+/g, '') || '',
            secondary_country_code: member.altNumberCountryCode || '',
            email: member.email || '',
            amount: memberAmount,
            amount_paid_till_date: member.amountPaid
              ? roundTo2(Number(member.amountPaid))
              : 0,
            total_amount: member.totalFee
              ? roundTo2(Number(member.totalFee))
              : 0,
            ...(isFeeCategoryEnabled &&
              member.feeCategoryData && {
                member_fee_configuration: getFeeCategoryData(
                  feeCategories,
                  groupFeeConfigurations || [],
                  memberInfo[index]
                ).feeConfigData.map((item: any) => ({
                  branch_fee_category_id: item.id,
                  amount:
                    item.value !== null &&
                    item.value !== undefined &&
                    item.value !== ''
                      ? roundTo2(Number(item.value))
                      : item.value
                }))
              }),
            admission_number: member.admissionNumber || null,
            admission_date: member.admissionDate || null,
            date_of_birth: member.dateOfBirth || null,
            guardian_name: member.guardianName || null,
            age: member.age || null,
            address_line_1: member.addressLine1 || null,
            address_line_2: member.addressLine2 || null,
            city: member.city || null,
            state: member.state || null,
            country: member.country || null,
            pincode: member.pincode?.toString() || null,
            custom_fields: (customFields || []).map((field) => {
              const camelKey = snakeToCamel(field.key);
              const rawValue = (member as Record<string, any>)[camelKey];
              if (field.type === 'bool' && rawValue) {
                return {
                  key: field.key,
                  value:
                    `${rawValue}`.toLowerCase() === 'yes' ? 'true' : 'false'
                };
              }
              return {
                key: field.key,
                value: rawValue ? `${rawValue}` : null
              };
            }),
            // For attendance-based fees, build payment_schedule with interval and billing config
            ...(isAttendanceBasedFee
              ? {
                  payment_schedule: {
                    interval: isSessionBased
                      ? PaymentCollectionType.SESSION_BASED
                      : PaymentCollectionType.MONTHLY,
                    interval_frequency: 1,
                    ...(isSessionBased
                      ? {
                          sessions_per_cycle:
                            member.sessionsPerCycle ||
                            groupSessionsPerCycle ||
                            0
                        }
                      : {
                          ...(member.startDate && {
                            start_date: formatISODate({
                              date: member.startDate,
                              separator: '-',
                              format: 'yyyy-mm-dd'
                            }),
                            monthly_config: getMonthConfigFromDate(
                              member.startDate
                            )
                          })
                        })
                  }
                }
              : {
                  // For ONCE payment type without immediate payment
                  ...(isOnce &&
                    !isImmediatePayment &&
                    !isCustomTerm &&
                    member.dueDate && {
                      activation_date: member.dueDate
                    }),
                  // For non-ONCE payment types, include payment_schedule
                  ...(!isOnce && {
                    payment_schedule: getFileImportPaymentSchedule(
                      member as MemberListType,
                      isCustomTerm || false,
                      member.termsList,
                      isFeeCategoryEnabled,
                      groupStartDate,
                      groupFrequency,
                      groupIntervalFrequency
                    )
                  })
                }),
            ...(member.discount !== undefined && {
              discount: member.discount
            })
          };
        });

        try {
          const response = await addMemberToGroupV3({
            params: { organizationId, branchId, groupId },
            body: {
              members_added: validMembersPayload,
              force_create: false
            }
          });

          if ('data' in response && response.data?.data?.members_added) {
            const { members_added: membersAdded } = response.data.data;

            // Merge BE errors from members response
            const mergedMembers = mergeBackendErrors(
              membersData,
              membersAdded.members || [],
              membersAdded.warnings || [],
              feValidIndices
            );

            // Check if there are any errors after merging
            const hasAnyErrors = mergedMembers.some(
              (m) => m.errors && m.errors.length > 0
            );
            const hasWarnings =
              membersAdded.warnings && membersAdded.warnings.length > 0;

            // If no errors and no warnings, directly add members to list
            if (!hasAnyErrors && !hasWarnings) {
              createSuccessAlert('File uploaded successfully');
              trackImportFileUploaded(selectedFile, 'success');
              onProceed(updatedMemberList);
              // Reset state but keep file visible
              setMembersWithErrors([]);
              setWarningRecords([]);
              setFormattedMembers([]);
              setTimeout(() => {
                setIsLoading(false);
                setTimeLeft(0);
              }, 500);
              return;
            }

            // Has errors or warnings, show modal
            setMembersWithErrors(mergedMembers);

            // Store warning records
            if (hasWarnings && membersAdded.warnings) {
              setWarningRecords(membersAdded.warnings);
            }

            // Determine which step to show:
            // - If there are errors, show error_preview first
            // - If only warnings (no errors), go directly to warning_preview
            if (hasAnyErrors) {
              setStep('error_preview');
            } else {
              // No errors, only warnings - go directly to warning preview
              setStep('warning_preview');
            }
            trackImportFileUploaded(selectedFile, 'success');
            createSuccessAlert('File uploaded successfully');
            setTimeout(() => {
              setIsLoading(false);
              setTimeLeft(0);
            }, 500);
            return;
          }
          // API error occurred, just use FE validation results
          setMembersWithErrors(membersData);
        } catch (error) {
          // API call failed, use FE validation results only
          setMembersWithErrors(membersData);
        }
      } else {
        // All rows have FE errors, no BE call needed
        setMembersWithErrors(membersData);
      }

      // Check if we need to show modal for FE errors only (no BE call was made or BE call failed)
      const hasAnyFEErrors = membersData.some(
        (m) => m.errors && m.errors.length > 0
      );

      if (hasAnyFEErrors) {
        // Go to error preview step for FE errors
        setStep('error_preview');
        trackImportFileUploaded(selectedFile, 'success');
        createSuccessAlert('File uploaded successfully');
      } else {
        // No FE errors and no BE call was made (shouldn't happen normally)
        trackImportFileUploaded(selectedFile, 'success');
        createSuccessAlert('File uploaded successfully');
        onProceed(updatedMemberList);
        setMembersWithErrors([]);
        setFormattedMembers([]);
      }
    } catch (error) {
      setFileUploadError('Error parsing file');
      trackImportFileUploaded(selectedFile, 'failure', 'error_parsing_file');
    }

    setTimeout(() => {
      setIsLoading(false);
      setTimeLeft(0);
    }, 500);
  };

  const checkFileValidityAndProcess = (selectedFile: File) => {
    if (
      selectedFile?.type !== 'text/csv' &&
      selectedFile.type !==
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      setFileUploadError('Please provide a csv or xlsx file to upload!');
      trackImportFileUploaded(selectedFile, 'failure', 'invalid_file_type');
      return false;
    }

    if (selectedFile.size > allowedCSVSizeForGroupCreation) {
      setFileUploadError('Upload failed. File size more than 2MB');
      trackImportFileUploaded(selectedFile, 'failure', 'file_too_large');
      return false;
    }

    try {
      setFileName(selectedFile.name);
      setFileSizeInMB(bytesToMB(selectedFile.size || 0));
      setIsLoading(true);
      setTimeLeft(Math.ceil(selectedFile.size / 100000));
      setTotalTime(Math.ceil(selectedFile.size / 100000));

      const reader = new FileReader();
      reader.onload = async (event: any) => {
        let rowsInFile = 0;
        if (selectedFile.type === 'text/csv') {
          const csvData = event.target.result as string;
          rowsInFile = csvData.split('\n').filter((data) => data).length - 1;
        } else {
          const parsedData = await parseXLSXFile(selectedFile);
          const memberInfo = parsedData.data as unknown as AddMemberInput[];
          rowsInFile = memberInfo.length;
        }

        if (rowsInFile > GROUP_MEMBER_MAX_COUNT) {
          setIsLoading(false);
          setFileUploadError(
            `Maximum no. of members allowed in a single file upload is ${GROUP_MEMBER_MAX_COUNT}`
          );
          trackImportFileUploaded(selectedFile, 'failure', 'too_many_members');
        } else {
          await handleFileProcessing(selectedFile);
        }
      };
      reader.readAsText(selectedFile);
    } catch (error) {
      setFileUploadError('Error reading file');
      trackImportFileUploaded(selectedFile, 'failure', 'error_reading_file');
      return false;
    }
    return true;
  };

  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e?.target?.files?.[0];
    if (selectedFile) {
      checkFileValidityAndProcess(selectedFile);
      e.target.value = '';
    }
  };

  const handleProceedClick = () => {
    // Build a set of indices that have errors
    const errorIndices = new Set<number>();
    membersWithErrors.forEach((member, index) => {
      if (member.errors && member.errors.length > 0) {
        errorIndices.add(index);
      }
    });

    // Filter out members with errors and call onProceed with valid members
    const validMembers = formattedMembers.filter(
      (_, index) => !errorIndices.has(index)
    );

    if (validMembers.length === 0) {
      createErrorAlert('No valid members to add');
      return;
    }

    // Filter rows to only show valid members in "View File"
    const validRows = rows.filter((_, index) => !errorIndices.has(index));

    sendMixpanelEventV2(MixpanelEventV2.GROUP_MEMBER_IMPORT_FILE_PROCEEDED, {
      file_type: file ? getImportFileType(file) : undefined,
      ...getImportRowCounts(membersWithErrors),
      ...mixpanelGroupContextRef.current
    });
    onProceed(validMembers);
    // Reset step and validation data
    setStep('upload');
    setMembersWithErrors([]);
    setWarningRecords([]);
    setFormattedMembers([]);
    // Update rows to only show valid members for "View File" functionality
    setRows(validRows);
    resetApiState();
  };

  // Todo: check the condition here
  const handleNextClick = () => {
    if (step === 'error_preview') {
      // Check if there are warning records to review
      if (warningRecords.length > 0) {
        setStep('warning_preview');
      } else {
        // No warnings, proceed directly
        handleProceedClick();
      }
    }
  };

  // Calculate counts for display
  const totalMembersCount = membersWithErrors.length;
  const membersWithErrorsCount = membersWithErrors.filter(
    (m) => m.errors && m.errors.length > 0
  ).length;
  const membersWithoutErrorsCount = totalMembersCount - membersWithErrorsCount;
  const warningCount = warningRecords.length;

  // Transform data for ParsedDataGrid
  // For error_preview: use original rows data (matches file headers)
  // For warning_preview: transform backend warning records to display format
  const displayRows = useMemo(
    () =>
      step === 'warning_preview'
        ? warningRecords.map(transformMemberResponseToDisplayRow)
        : rows.map(transformInputRowToDisplayRow),
    [step, rows, warningRecords]
  );

  const displayHeaders = useMemo(
    () =>
      step === 'warning_preview'
        ? getWarningPreviewHeaders(!!isAttendanceBasedFee, customFields)
        : headers,
    [step, headers, isAttendanceBasedFee, customFields]
  );

  const rowErrorsById = useMemo(
    () =>
      step === 'warning_preview'
        ? transformErrorsToRowErrorsById(warningRecords)
        : transformErrorsToRowErrorsById(membersWithErrors),
    [step, membersWithErrors, warningRecords]
  );

  // Modal title
  const modalTitle = useMemo(() => {
    if (file) {
      return fileName;
    }
    return 'Upload CSV/Excel';
  }, [file, fileName]);

  // Button labels
  const primaryButtonLabel = useMemo(() => {
    if (step === 'upload') return undefined;
    if (step === 'warning_preview') return 'Proceed';
    // Show "Proceed" if no warning records (will directly add to list)
    if (step === 'error_preview' && warningRecords.length === 0) {
      return 'Proceed';
    }
    return 'Next';
  }, [step, warningRecords.length]);

  const isPrimaryButtonDisabled =
    step === 'upload' ||
    isApiLoading ||
    (step === 'error_preview' && membersWithoutErrorsCount === 0);

  return {
    // Step state
    step,

    // File state
    file,
    fileName,
    fileSizeInMB,
    fileUploadError,
    isLoading,
    timeLeft,
    totalTime,
    showPrimaryNumberWarning,
    headers, // Original file headers for View File
    displayHeaders, // Headers for modal display
    rows,

    // Response data
    displayRows,
    rowErrorsById,

    // Computed counts
    totalMembers: totalMembersCount,
    membersWithErrors: membersWithErrorsCount,
    membersWithoutErrors: membersWithoutErrorsCount,
    warningCount,

    // UI labels
    modalTitle,
    primaryButtonLabel,
    isPrimaryButtonDisabled,

    // Loading state
    isApiLoading,

    // Handlers
    onFileSelect,
    checkFileValidityAndProcess,
    deleteFile,
    handleCancel,
    handleNextClick,
    handleProceedClick
  };
};
